import { useIntl } from 'react-intl';

import { useOptionalMessage } from 'Hooks/useOptionalMessage';
import { appConfigStore } from 'Models/AppConfigStore';
import { getMessageByKey } from 'Stores/LanguageStore';
import { stringNotEmpty } from 'Util/TypeGuards';
import { assignDriverDetailsToPassenger } from 'Views/Components/_HumanWritten/BookingWizard/FerryTripBookingWizard/BookingWizardData/PassengerDetail';
import { getTotalPassengerCountFromWizardData } from 'Views/Components/_HumanWritten/BookingWizard/FerryTripBookingWizard/WizardSteps/Passengers/PassengerTabHelpers';
import { Tag } from 'Views/Components/_HumanWritten/CheckIn/FerryCheckIn/CheckInList/Tag';
import If from 'Views/Components/If/If';
import Icon from 'Views/Components/_HumanWritten/Icon/Icon';
import { TextInput } from 'Views/Components/New/TextInput';
import { PhoneInput } from 'Views/Components/New/PhoneInput';
import { BookingWizardData } from '../../BookingWizardData/BaseBookingWizardData';
import { isVehicleBookingWizardData, VehicleBookingWizardData } from '../../BookingWizardData/VehicleBookingWizardData';
import { useBookingWizardDataStore } from '../../BookingWizardDataStore';
import { WizardErrors } from '../../BookingWizardTypes';

import { useVehicleTabContext } from './VehicleTabContext';

export function VehicleDriverDetailsForm() {
	const { previousCargoList } = useVehicleTabContext();
	const intl = useIntl();
	const fmtOptional = useOptionalMessage();
	const { wizardData, setWizardData, innerWizardErrors, setInnerWizardErrors } = useBookingWizardDataStore();
	const { driverPhoneRequired, driverPhoneEnabled } = appConfigStore;

	if (!isVehicleBookingWizardData(wizardData.innerWizardData)) {
		return <></>;
	}

	const passengerCount = getTotalPassengerCountFromWizardData(wizardData);
	const typedErrors = innerWizardErrors as WizardErrors<VehicleBookingWizardData>;
	const inner = wizardData.innerWizardData;

	const updateField = <K extends keyof VehicleBookingWizardData>(field: K, value: VehicleBookingWizardData[K]) => {
		const newData = new BookingWizardData({ ...wizardData });
		if (isVehicleBookingWizardData(newData.innerWizardData)) {
			(newData.innerWizardData as any)[field] = value;
			if (field === 'driverFirstName' && appConfigStore.passengerFirstNameEnabled && passengerCount > 0) {
				assignDriverDetailsToPassenger(newData);
			}
			if (field === 'driverLastName' && appConfigStore.passengerLastNameEnabled && passengerCount > 0) {
				assignDriverDetailsToPassenger(newData);
			}
			if (field === 'driverPhone' && appConfigStore.passengerPhoneEnabled && passengerCount > 0) {
				assignDriverDetailsToPassenger(newData);
			}
		}
		setWizardData(newData);

		const newErrors = { ...typedErrors };
		delete (newErrors as any)[field];
		setInnerWizardErrors(newErrors);
	};

	if (
		(stringNotEmpty(inner.cargoMake) && previousCargoList.length > 0) ||
		previousCargoList.length === 0 ||
		!appConfigStore.previousVehicleSelectionEnabled
	) {
		return (
			<section className="driver-details-section booking-section-card">
				{(() => {
					const titleWord =
						passengerCount <= 0
							? intl.formatMessage({
									id: 'vehicle-tab__driverless-vehicle__contact-section__title',
									defaultMessage: 'Contact',
								})
							: intl.formatMessage({
									id: 'vehicle-tab__driver-details__contact-section__title',
									defaultMessage: 'Driver',
								});
					const description =
						passengerCount <= 0
							? fmtOptional({
									id: 'vehicle-tab__driverless-vehicle__contact-section__description',
									defaultMessage:
										'We may need to contact the person who ' +
										'will be receiving the vehicle on the other end of the trip.',
								})
							: fmtOptional({
									id: 'vehicle-tab__driver-details__contact-section__description',
									defaultMessage: 'We need the details of the person driving the vehicle.',
								});
					return (
						<header className="booking-section-header">
							<span className="booking-section-heading__icon">
								<Icon name="person" useMaterialIcons />
							</span>
							<div className="booking-section-header__text">
								<h4 className="booking-section-title">
									{intl.formatMessage(
										{
											id: 'vehicle-tab__driver-details__section-heading',
											defaultMessage: '{role} details',
										},
										{ role: titleWord },
									)}
									<If condition={passengerCount <= 0}>
										<Tag text={getMessageByKey('driverless-vehicle__tag', 'Driverless')} />
									</If>
								</h4>
								{description && <p className="booking-section-description">{description}</p>}
							</div>
						</header>
					);
				})()}
				<div className="driver-details-body grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
					<TextInput
						id="driver-first-name"
						label={intl.formatMessage({
							id: 'vehicle-tab__driver-details__first-name__label',
							defaultMessage: 'First name',
						})}
						placeholder={intl.formatMessage({
							id: 'vehicle-tab__driver-details__first-name__placeholder',
							defaultMessage: 'First name',
						})}
						required
						value={inner.driverFirstName ?? ''}
						error={typedErrors?.driverFirstName}
						onChange={e => updateField('driverFirstName', e.target.value)}
					/>
					<TextInput
						id="driver-last-name"
						label={intl.formatMessage({
							id: 'vehicle-tab__driver-details__last-name__label',
							defaultMessage: 'Last name',
						})}
						placeholder={intl.formatMessage({
							id: 'vehicle-tab__driver-details__last-name__placeholder',
							defaultMessage: 'Last name',
						})}
						required
						value={inner.driverLastName ?? ''}
						error={typedErrors?.driverLastName}
						onChange={e => updateField('driverLastName', e.target.value)}
					/>
					{driverPhoneEnabled && (
						<PhoneInput
							id="driver-phone"
							label={intl.formatMessage({
								id: 'vehicle-tab__driver-details__phone__label',
								defaultMessage: 'Phone',
							})}
							placeholder={intl.formatMessage({
								id: 'vehicle-tab__driver-details__phone__placeholder',
								defaultMessage: 'Phone',
							})}
							required={driverPhoneRequired}
							value={inner.driverPhone ?? ''}
							error={typedErrors?.driverPhone}
							onChange={value => updateField('driverPhone', value)}
						/>
					)}
					{/* The original "Additional information" note now renders in the
					    DynamicFieldsSection ("Additional information" section) in VehicleTabInner. */}
				</div>
			</section>
		);
	}

	return <></>;
}
